// src/components/forms/AnalyticsManager.js
import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';

import RenderDefaultAnalytics from './RenderDefaultAnalytics'
import DataTrend from '../svgIcons/dataTrend';

import API_CONFIG from '../../config/api'
const BASE_URL = API_CONFIG.BASE_URL

function AnalyticsManager({ surveyId }) {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('default');
	const [surveyInfos, setSurveyInfos] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	
	const { authenticatedFetch } = useAuthenticatedApi();

	const handleBackClick = () => {
		// Navigate back to the previous page (dashboard)
		navigate(-1);
	};

	useEffect(() => {
		getSurveyInfo();
	}, [surveyId]);

	const getSurveyInfo = async () => {
		setIsLoading(true);
		setError(null); // Clear previous errors

		try {
			const endpoint = `${BASE_URL}/surveys/survey-info-by-id/${surveyId}`;
			const result = await authenticatedFetch(endpoint, {}, "GET");
			if (result.status === 404) {
				navigate('/404', { state: { message: `Survey with ID "${surveyId}" not found.`} });
				return;
			}
			if (result.status === 401) {
				// Unauthorized access (participant token invalid or survey inactive)
				// TODO better error handling for user experience (et the moment display in red the message below)
				setError("You are not authorized to access this survey or you are not authenticated");
				setSurveyInfos(null); // Clear survey data on unauthorized
				return;
			}
			if (!result.ok) {
				// Handle other non-2xx responses
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Response body isn't JSON? Ignore and keep generic error
				}
				console.error("AnalyticsManager API Error:", errorMessage, result);
				setError(errorMessage);
				setSurveyInfos(null); // Clear model on error
				return;
			}

			const data = await result.json();
			if (data) {
				setSurveyInfos(data);
			} else {
				console.error("API returned empty or invalid data structure:", data);
				setError("Survey data is empty or malformed.");
				setSurveyInfos([]);
			}
		} catch (err) {
			console.error("Network or unexpected error while fetching survey:", err);
			setError("Failed to load survey. Please check your connection and try again.");
			setSurveyInfos([]);
		} finally {
			setIsLoading(false);
		}
	}

	const formatDate = (dateString) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch (error) {
			return dateString;
		}
	};

	const truncateDescription = (description, maxLength = 128) => {
		if (!description) return '';
		if (description.length <= maxLength) return description;
		return description.substring(0, maxLength) + '...';
	};

	const renderAdHocAnalysis = () => {
		return (
			<div className="p-8 text-center text-gray-500">
				<p>Ad hoc Analysis - Not implemented yet</p>
			</div>
		);
	};

	const renderAIAnalyzer = () => {
		return (
			<div className="p-8 text-center text-gray-500">
				<p>AI Analyzer - Not implemented yet</p>
			</div>
		);
	};

	return (
		<div className="min-h-screen bg-gray-50 p-2 animate-in slide-in-from-right duration-300">
			<div className="max-w-full mx-auto">
				{/* Header Section */}
				<div className="bg-white rounded-lg shadow-sm border p-4 mb-3">
					<div className="flex items-center gap-4">
						<button
							onClick={handleBackClick}
							className="flex items-center justify-center w-10 h-10 rounded-full border hover:bg-gray-100 transition-colors"
							title="Go back"
						>
							<ArrowLeft className="h-5 w-5 text-gray-600" />
						</button>
						
						<div className="flex-1 ">
							
							<h1 className="text-2xl font-bold text-gray-900 flex items-center justify-left">
								<DataTrend className="w-8 h-8 mr-2" />
								Analytics Manager
							</h1>
							{isLoading ? (
								<div className="mt-2 space-y-2">
									<div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
									<div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
								</div>
							) : surveyInfos && !error ? (
								<div className="mt-2 space-y-1">
									<p className="text-lg font-medium text-gray-800">
										{surveyInfos.title}
									</p>
									{surveyInfos.description && (
										<p className="text-sm text-gray-600">
											{truncateDescription(surveyInfos.description)}
										</p>
									)}
									<div className="flex items-center gap-4 text-sm text-gray-500">
										<span>Survey ID: {surveyInfos.survey_id}</span>
										<span>Created: {formatDate(surveyInfos.created_at)}</span>
									</div>
								</div>
							) : (
								<p className="text-sm text-gray-500 mt-1">
									Survey ID: {surveyId}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Main Content Area - Tabbed Interface */}
				<div className="bg-white rounded-lg shadow-sm border overflow-hidden">
					{error ? (
						// Error State
						<div className="p-4">
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<div className="flex">
									<div className="flex-shrink-0">
										<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
											<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
										</svg>
									</div>
									<div className="ml-3">
										<h3 className="text-sm font-medium text-red-800">
											Error Loading Survey
										</h3>
										<p className="mt-1 text-sm text-red-700">
											{error}
										</p>
									</div>
								</div>
							</div>
						</div>
					) : (
						<>
							{/* Tab Navigation */}
							<div className="border-b border-gray-200">
								<nav className="flex space-x-8" aria-label="Tabs">
									<button
										onClick={() => setActiveTab('default')}
										className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'default'
											? 'text-gray-800 text-blue-600'
											: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Default
									</button>
									<button
										onClick={() => setActiveTab('adhoc')}
										className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'adhoc'
											? 'text-gray-800 text-blue-600'
											: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Ad hoc Analysis
									</button>
									<button
										onClick={() => setActiveTab('ai')}
										className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'ai'
											? 'text-gray-800 text-blue-600'
											: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										AI Analyzer
									</button>
								</nav>
							</div>

							{/* Tab Content */}
							<div className="p-3">
								{activeTab === 'default' && (
								<div>
									<RenderDefaultAnalytics surveyId={surveyId} />
								</div>
								)}
								{activeTab === 'adhoc' && renderAdHocAnalysis()}
								{activeTab === 'ai' && renderAIAnalyzer()}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default AnalyticsManager;